/**
 * ============================================================
 * KEJJA OS — src/pages/finance/BudgetsPage.tsx
 * Set monthly spending caps per category.
 * ============================================================
 *
 * Shows every expense category with an input for its monthly
 * cap. Fetches the overview alongside so the user sees
 * current spend vs the cap they're setting.
 * ============================================================
 */

import { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Save, Target, Trash2 } from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceCategories, useFinanceBudgets, useFinanceOverview,
  useUpsertBudget, useDeleteBudget,
} from '@/hooks/useFinance';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney, formatMoneyCompact, parseMoneyToMinor } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function BudgetsPage() {
  const { homeId, currencyCode } = useFinanceContext();
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);

  const { data: catsData, isLoading: catsLoading } = useFinanceCategories(homeId);
  const { data: budgetsData } = useFinanceBudgets(homeId, year, month);
  const { data: overview } = useFinanceOverview(homeId, year, month);

  const categories = catsData?.categories ?? [];
  const budgets = budgetsData?.budgets ?? [];
  const budgetMap = new Map(budgets.map((b) => [b.categoryId, b]));
  const spendMap = new Map(
    (overview?.byCategory ?? []).map((c) => [c.categoryId, c.totalOut]),
  );

  const expenseCats = categories.filter((c) => c.kind === 'expense');

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1);
  };

  return (
    <div className="space-y-5 max-w-3xl">
      {/* ============ HEADER + MONTH SWITCHER ============ */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
            <Target className="w-3.5 h-3.5" />
            Budgets
          </div>
          <p className="text-sm text-muted-foreground">
            Set a monthly cap per category. Overview page shows how you're tracking.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-sm min-w-[130px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ============ LIST ============ */}
      {catsLoading ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14" />)}
          </CardContent>
        </Card>
      ) : expenseCats.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No expense categories yet. Create some on the Categories tab first.
          </CardContent>
        </Card>
      ) : (<>
          <BudgetTotalsFooter
            budgets={budgets}
            spendMap={spendMap}
            currencyCode={currencyCode}
          />
          <Card>
          <CardContent className="p-0 divide-y divide-border">
            {expenseCats.map((cat) => (
              <BudgetRow
                key={cat.id}
                homeId={homeId}
                category={cat}
                currencyCode={currencyCode}
                year={year}
                month={month}
                budgetAmount={budgetMap.get(cat.id)?.amount ?? null}
                budgetId={budgetMap.get(cat.id)?.id ?? null}
                spent={spendMap.get(cat.id) ?? 0}
              />
            ))}
          </CardContent>
        </Card>
        </>
      )}

      <p className="text-[11px] text-muted-foreground text-center">
        Caps are per month. Leave blank to remove a cap.
      </p>
    </div>
  );
}

/* ============ row ============ */

function BudgetRow({
  homeId, category, currencyCode, year, month,
  budgetAmount, budgetId, spent,
}: {
  homeId: string;
  category: { id: string; name: string; color: string | null };
  currencyCode: string;
  year: number;
  month: number;
  budgetAmount: number | null;
  budgetId: string | null;
  spent: number;
}) {
  const upsert = useUpsertBudget(homeId);
  const del = useDeleteBudget(homeId);
  const [draft, setDraft] = useState<string>(
    budgetAmount !== null ? String(budgetAmount / 100) : '',
  );

  // Refresh input when parent data changes (month nav, revalidation)
  useEffect(() => {
    setDraft(budgetAmount !== null ? String(budgetAmount / 100) : '');
  }, [budgetAmount]);

  const save = () => {
    const minor = parseMoneyToMinor(draft, currencyCode);
    if (minor === null || minor < 0) return;
    upsert.mutate({
      categoryId: category.id,
      amount: minor,
      currencyCode,
      periodYear: year,
      periodMonth: month,
    });
  };

  const remove = () => {
    if (!budgetId) { setDraft(''); return; }
    if (confirm(`Remove budget for ${category.name}?`)) del.mutate(budgetId);
  };

  const pct = budgetAmount && budgetAmount > 0
    ? Math.round((spent / budgetAmount) * 100)
    : 0;
  const over = pct > 100;
  const currentMinor = parseMoneyToMinor(draft, currencyCode);
  const dirty = currentMinor !== null && currentMinor !== (budgetAmount ?? -1);

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{
          backgroundColor: category.color ? `${category.color}20` : 'var(--muted)',
          color: category.color ?? 'var(--muted-foreground)',
        }}
      >
        {category.name[0]?.toUpperCase() ?? '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-sm truncate">{category.name}</span>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            Spent {formatMoneyCompact(spent, currencyCode)}
          </span>
        </div>
        {budgetAmount !== null && budgetAmount > 0 && (
          <>
            <div className="flex items-center justify-between gap-2 mt-1">
              <span className={cn(
                'text-[11px] tabular-nums',
                over ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-muted-foreground',
              )}>
                {pct}% of {formatMoney(budgetAmount, currencyCode)}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  over ? 'bg-rose-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500',
                )}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground pointer-events-none">
            {currencyCode === 'KES' ? 'KSh' : currencyCode}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="—"
            className="w-28 h-9 rounded-lg border border-border bg-background pl-10 pr-2 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500"
          />
        </div>
        <Button
          size="icon"
          variant={dirty ? 'default' : 'ghost'}
          onClick={save}
          disabled={!dirty || upsert.isPending}
          className={cn('h-9 w-9', dirty && 'bg-orange-600 hover:bg-orange-700 text-white')}
          aria-label="Save"
        >
          <Save className="w-4 h-4" />
        </Button>
        {budgetId && (
          <Button
            size="icon"
            variant="ghost"
            onClick={remove}
            disabled={del.isPending}
            className="h-9 w-9 text-muted-foreground hover:text-rose-600"
            aria-label="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ============ Budget totals footer ============ */
function BudgetTotalsFooter({
  budgets, spendMap, currencyCode,
}: {
  budgets: Array<{ categoryId: string; amount: number }>;
  spendMap: Map<string | null, number>;
  currencyCode: string;
}) {
  const totalBudget = budgets.reduce((sum, b) => sum + Number(b.amount), 0);
  const totalSpent = budgets.reduce(
    (sum, b) => sum + Number(spendMap.get(b.categoryId) ?? 0),
    0,
  );
  const pct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
  const over = pct > 100;
  const remaining = totalBudget - totalSpent;

  if (totalBudget === 0) return null;

  return (
    <Card className="mb-3 border-orange-200 dark:border-orange-900/40 bg-gradient-to-br from-orange-50/40 to-amber-50/30 dark:from-orange-950/10 dark:to-amber-950/10">
      <CardContent className="p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Total budget
            </div>
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {formatMoney(totalBudget, currencyCode)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Spent
            </div>
            <div className={cn(
              'text-2xl font-bold tabular-nums',
              over ? 'text-rose-600 dark:text-rose-400' : 'text-foreground',
            )}>
              {formatMoney(totalSpent, currencyCode)}
            </div>
          </div>
        </div>

        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              over ? 'bg-rose-500' : pct > 80 ? 'bg-amber-500' : 'bg-emerald-500',
            )}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-[11px]">
          <span className={cn(
            'tabular-nums font-medium',
            over ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
          )}>
            {pct}% used
          </span>
          <span className="tabular-nums text-muted-foreground">
            {over
              ? `${formatMoney(totalSpent - totalBudget, currencyCode)} over`
              : `${formatMoney(remaining, currencyCode)} left`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

